<template>
  <div class="invoice-report">
    <v-card flat outlined class="report-table-card">
      <v-card-title class="report-table-header">
        <div>
          <div class="report-title">Invoice Details</div>

          <div class="report-subtitle">
            {{ totalItems }} invoices found. Showing {{ Invoice.length }}.
          </div>
        </div>

        <v-spacer />

        <v-btn
          v-if="hasMoreInvoices"
          rounded
          outlined
          small
          color="primary"
          class="mr-3 no-print"
          :loading="isLoadingMore"
          @click="getInvoiceDetail(true)"
        >
          Add {{ limit }} More
        </v-btn>

        <div class="summary-box">
          <div class="summary-label">Loaded Total</div>
          <div class="summary-value">{{ invoiceTotal | currency }}</div>
        </div>

        <div class="summary-box">
          <div class="summary-label">Payment</div>
          <div class="summary-value">{{ paymentTotal | currency }}</div>
        </div>

        <div class="summary-box">
          <div class="summary-label">Due</div>
          <div class="summary-value due">{{ dueTotal | currency }}</div>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <v-progress-linear v-if="isLoading" indeterminate height="3" />

        <v-data-table
          v-else
          dense
          :headers="headers"
          :items="Invoice"
          :items-per-page="-1"
          hide-default-footer
          :mobile-breakpoint="0"
          class="invoice-table"
        >
          <template v-slot:item.localId="{ item }">
            <a
              href="#"
              class="invoice-link"
              @click.prevent="viewInvoice(item.id)"
            >
              {{ item.localId }}
            </a>
          </template>

          <template v-slot:item.date="{ item }">
            {{ item.date | moment("MM/DD/YYYY") }}
          </template>

          <template v-slot:item.customer="{ item }">
            <span class="text-capitalize">
              {{ item.Customer ? item.Customer.name : "" }}
            </span>
          </template>

          <template v-slot:item.total="{ item }">
            <span class="money">{{ item.total | currency }}</span>
          </template>

          <template v-slot:item.payment="{ item }">
            <span class="money">{{ item.payment | currency }}</span>
          </template>

          <template v-slot:item.due="{ item }">
            <span class="money due">{{ item.due | currency }}</span>
          </template>

          <template v-slot:no-data>
            <div class="empty-state">
              No invoices found for this date range.
            </div>
          </template>
        </v-data-table>

        <div v-if="hasMoreInvoices" class="load-more-footer no-print">
          <div class="load-more-text">
            Showing {{ Invoice.length }} of {{ totalItems }} invoices.
          </div>

          <v-btn
            rounded
            color="primary"
            dark
            :loading="isLoadingMore"
            @click="getInvoiceDetail(true)"
          >
            Load {{ limit }} More
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import invoiceService from "@/modules/Invoice/service.js";
import Hashids from "hashids";

const hashids = new Hashids();

export default {
  name: "invoice-report",

  props: [
    "SalesRepUserId",
    "CustomerId",
    "hideProfit",
    "hideCost",
    "start",
    "end",
  ],

  data() {
    return {
      isLoading: true,
      isLoadingMore: false,
      Invoice: [],
      limit: 500,
      offset: 0,
      totalItems: 0,
      headers: [
        {
          text: "Invoice #",
          align: "left",
          value: "localId",
          sortable: true,
        },
        {
          text: "Date",
          align: "left",
          value: "date",
          sortable: true,
        },
        {
          text: "Customer",
          align: "left",
          value: "customer",
          sortable: true,
        },
        {
          text: "Amount",
          align: "right",
          value: "total",
          sortable: true,
        },
        {
          text: "Payment",
          align: "right",
          value: "payment",
          sortable: true,
        },
        {
          text: "Due",
          align: "right",
          value: "due",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    exportRows() {
      return this.Invoice.map((item) => ({
        localId: item.localId,
        date: item.date,
        customerName: item.Customer ? item.Customer.name : "",
        total: item.total,
        payment: item.payment,
        due: item.due,
      }));
    },
    hasMoreInvoices() {
      return this.totalItems > 0 && this.Invoice.length < this.totalItems;
    },

    invoiceTotal() {
      return this.Invoice.reduce(
        (sum, item) => sum + Number(item.total || 0),
        0
      );
    },

    paymentTotal() {
      return this.Invoice.reduce(
        (sum, item) => sum + Number(item.payment || 0),
        0
      );
    },

    dueTotal() {
      return this.Invoice.reduce((sum, item) => sum + Number(item.due || 0), 0);
    },
  },

  async created() {
    await this.getInvoiceDetail();
  },

  methods: {
    async getInvoiceDetail(loadMore = false) {
      if (loadMore && this.isLoadingMore) return;

      if (loadMore) {
        this.isLoadingMore = true;
      } else {
        this.isLoading = true;
        this.offset = 0;
        this.Invoice = [];
        this.totalItems = 0;
      }

      const where = {
        limit: this.limit,
        offset: this.offset,
      };

      if (this.start) where.start = this.start;
      if (this.end) where.end = this.end;
      if (this.CustomerId) where.CustomerId = this.CustomerId;
      if (this.SalesRepUserId) where.SalesRepUserId = this.SalesRepUserId;

      try {
        const response = await invoiceService.getAll(where);
        const payload = response.data || {};

        console.log("InvoiceDetail payload", response);

        const rows = response.data;
        const count = Number(response.count || 0);

        this.Invoice = loadMore ? [...this.Invoice, ...rows] : rows;
        this.$emit("export-data", this.exportRows);
        this.totalItems = count;
        this.offset = this.Invoice.length;
      } catch (error) {
        console.error("Invoice report error", error);
      } finally {
        this.$Progress.finish();
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    },

    viewInvoice(invoice) {
      const hashId = hashids.encode(invoice);

      const routeData = this.$router.resolve({
        path: this.$route.meta.public
          ? `/public/invoice/${hashId}/view`
          : `/global/invoice/${hashId}/view`,
      });

      window.open(routeData.href, "newwindow", "width=850,height=1100");
    },
  },
};
</script>

<style scoped>
.invoice-report {
  width: 100%;
}

.report-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.report-table-header {
  background: #fff;
  padding: 18px 20px;
}

.report-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.report-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
}

.summary-box {
  min-width: 110px;
  padding: 8px 12px;
  margin-left: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-align: right;
  background: #f9fafb;
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
}

.summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.summary-value.due,
.money.due {
  color: #b91c1c;
}

.invoice-link {
  font-weight: 700;
  text-decoration: none;
}

.money {
  font-weight: 600;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7280;
}

.load-more-footer {
  padding: 18px;
  text-align: center;
  background: #fff;
  border-top: 1px solid #e5e7eb;
}

.load-more-text {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 10px;
}

.invoice-table ::v-deep th {
  font-size: 12px;
  font-weight: 700;
  color: #374151 !important;
  background: #f9fafb;
  text-transform: uppercase;
}

.invoice-table ::v-deep td {
  font-size: 13px;
}

@media print {
  .report-table-card {
    border: none !important;
    border-radius: 0;
  }

  .report-table-header {
    padding: 10px 0;
  }

  .summary-box {
    border: none;
    background: #fff;
  }

  .no-print {
    display: none !important;
  }
}
</style>